import React, { useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from 'react-redux';
import Select from 'react-select';
import { fetchStatuses, fetchUsers, fetchDepartments, getPriorities, getTags, getTypes, resetChangedColumns } from "../../../../redux/actions";
import { colorOption, colorValue, colorValueSingle, imageOption } from '../../../SelectComponents';
import { ucFirst } from "../../../../helpers/generic";
import { Form } from "react-bootstrap";
import failAlert from "../../../../helpers/failAlert";
import { baseURL } from "../../../../helpers/baseURL";
import { headers } from "../../../../helpers/Requests";
import successAlert from "../../../../helpers/successAlert";
import DatePicker from "../../../../helpers/DatePicker";
import { earliestDueDate, MIN_DUE_DATE_DAYS_AHEAD } from "../../../../helpers/dueDate";
import ToastAlert from "../../../../helpers/ToastAlert";

const TicketProperties = ({ ticket, ticketIsLoading, onUpdate, onComplete }) => {

    const dispatch = useDispatch();
    const user = useSelector(state => state?.userReducer?.user);
    const isCustomer = user?.isCustomer || user?.is_customer;
    const isAdmin = user?.isAdmin || user?.is_admin;
    const isTicketOwner = !!user?.id && ticket?.created_by?.id === user?.id;
    const dueDateLocked = !!ticket?.due_date && !isAdmin && !isTicketOwner;
    const [isLoading, setIsLoading] = useState({});
    const [dueDateError, setDueDateError] = useState('');
    const isTransferredOut = !!ticket?.is_transferred_out;
    const statusName = String(ticket?.status?.name ?? "").toLowerCase();
    const isRejected = !!ticket?.is_rejected || statusName === "rejected";
    const isClosedTicket =
        !!ticket?.is_closed || statusName === "closed";
    const isClosed =
        isTransferredOut ||
        isClosedTicket ||
        isRejected ||
        !!ticket?.is_locked;

    const updatedTaskSelector = useSelector(state => {
        return state?.UpdatesTaskReducer;
    });

    useEffect(() => {
        var changedColumnsTaskSelector = updatedTaskSelector?.changedColumns;
        // console.log('changedColumnsTaskSelector::',changedColumnsTaskSelector);
        let changedColumnsToWatch = ["type_id", "priority_id", "status_id", "department_id", "due_date", "tags", "user_id", "assignees"];
        let formateColumnsToWatch = ["Type", "Priority", "Status", "Department", "Due Date", "Tags", "Assignee", "Assignees"];

        if (changedColumnsToWatch.some(key => changedColumnsTaskSelector[key])) {
            let filteredChangedColumns = Object.keys(changedColumnsTaskSelector).filter(key => changedColumnsToWatch.includes(key)).map((item) => {
                const index = changedColumnsToWatch.indexOf(item);
                return formateColumnsToWatch[index];
            });
            onComplete && onComplete();
            dispatch(resetChangedColumns());
            // successAlert(`${filteredChangedColumns.join(', ')} updated by ${updatedTaskSelector?.changedBy || 'System'}`);
            ToastAlert({
                text: `${filteredChangedColumns.join(', ')} updated by ${updatedTaskSelector?.changedBy || 'System'}`,
            });
        }
    }, [updatedTaskSelector]);

    const dataKey = {
        'status': 'statuses',
        'department': 'departments',
        'type': 'types',
        'priority': 'priorities',
        'tag': 'tags',
        'assignee': 'users',
        'assignees': 'users',
    }

    const data = {
        statuses: useSelector(state => state.statusesReducer.statuses),
        departments: useSelector(state => state.departmentsReducer.departments),
        priorities: useSelector(state => state.prioritiesReducer.priorities),
        types: useSelector(state => state.typesReducer.types),
        tags: useSelector(state => state.tagsReducer.tags),
        users: useSelector(state => state.usersReducer.users),
    }

    const dataFetchers = {
        statuses: fetchStatuses,
        departments: fetchDepartments,
        priorities: getPriorities,
        types: getTypes,
        tags: getTags,
        users: fetchUsers,
    };

    useEffect(() => {
        Object.keys(dataFetchers).forEach(key => {
            if (!isLoading[key] && data[key].length === 0) {
                dispatch(dataFetchers[key]());
                updateIsLoading(key, true)
            }
        });
    }, [dispatch, isLoading]);

    const updateIsLoading = (item, value) => {
        setIsLoading(prev => ({
            ...prev,
            [item]: value
        }));
    };

    const updateProperties = async (item, updateData) => {
        updateIsLoading(item, true);
        if (item === 'due_date') {
            setDueDateError('');
        }
        try {
            const response = await axios.patch(`${baseURL}ticket-update/${ticket.id}`, {
                [item]: updateData
            }, headers);
            successAlert('Ticket updated');
            // Merge updated fields into parent state only after the request succeeds
            if (onUpdate && response.data?.ticket) {
                onUpdate(response.data.ticket);
            }
            return response.data;
        } catch (error) {
            // A rejected due date is a form validation problem — show it on the
            // field, not as a failure toast.
            const dueDateFieldError = item === 'due_date'
                && error.response?.status === 422
                && error.response.data?.errors?.due_date?.[0];
            if (dueDateFieldError) {
                setDueDateError(dueDateFieldError);
                return;
            }
            failAlert(error.response?.data?.message || 'Something went wrong. Please try again later.');
        } finally {
            updateIsLoading(item, false);
        }
    };

    const handleChange = async (item, selectedValue) => {
        if (isClosed) {
            return;
        }
        const updateData = (item === 'tag' || item === 'assignees') ? (selectedValue ?? []).map(v => v.value) : (selectedValue?.value || null);
        await updateProperties(item, updateData);
    };

    const parseDate = (value) => {
        const date = new Date(value);
        const year = date.getFullYear();
        const month = ('0' + (date.getMonth() + 1)).slice(-2);
        const day = ('0' + date.getDate()).slice(-2);
        return { value: `${year}-${month}-${day}` };
    }

    const predefinedDates = [
        {
            label: 'Today',
            value: new Date()
        }, {
            label: 'Tomorrow',
            value: new Date(new Date().setDate(new Date().getDate() + 1))
        }, {
            label: 'Next Week',
            value: new Date(new Date().setDate(new Date().getDate() + 7))
        }
    ]

    const getLabeledOptions = (item, value) => {
        const options = data[dataKey[item]]?.map(row => {
            let option = {
                value: row.id,
                label: ucFirst(row.name),
            };
            if (item === 'assignee' || item === 'assignees') {
                option.image = row.profile_image;
                option.description = row.email;
            } else {
                option.color = row.color;
            }
            return option;
        }) || [];
        // if (item == 'assignees') {
        //     console.log(":::options:::", options, 'item:', item, 'data[dataKey[item]]:', data[dataKey[item]]);
        // }
        if (item === 'assignees') {
            const assignees = Array.isArray(value) ? value : [];
            const assigneeIds = assignees.map(a => a?.id ?? a);
            const selectedFromOptions = options.filter(row => assigneeIds.includes(row.value));
            const selectedIds = new Set(selectedFromOptions.map(row => row.value));
            // Include assignees from other workspaces (e.g. transferred tickets)
            // that are not in the current workspace user list.
            const foreignSelected = assignees
                .filter(a => a && typeof a === 'object' && a.id != null && !selectedIds.has(a.id))
                .map(a => ({
                    value: a.id,
                    label: ucFirst(a.name || `#${a.id}`),
                    image: a.profile_image,
                    description: a.email,
                }));
            return { options: [...options, ...foreignSelected], selected: [...selectedFromOptions, ...foreignSelected] };
        }

        // Department/type/priority/tags on transferred tickets may belong to the
        // destination workspace and won't appear in the source workspace lists.
        if (['department', 'type', 'priority'].includes(item)) {
            const id = value?.id ?? value ?? null;
            const selectedFromOptions = options.filter(row => row.value === id);
            if (selectedFromOptions.length > 0 || !value || typeof value !== 'object' || value.id == null) {
                return { options, selected: selectedFromOptions };
            }
            const foreign = {
                value: value.id,
                label: ucFirst(value.name || `#${value.id}`),
                color: value.color,
            };
            return { options: [...options, foreign], selected: [foreign] };
        }

        if (item === 'tag') {
            const tags = Array.isArray(value) ? value : [];
            const selectedFromOptions = options.filter(row => tags.some(tag => (tag?.id ?? tag) === row.value));
            const selectedIds = new Set(selectedFromOptions.map(row => row.value));
            const foreignSelected = tags
                .filter(tag => tag && typeof tag === 'object' && tag.id != null && !selectedIds.has(tag.id))
                .map(tag => ({
                    value: tag.id,
                    label: ucFirst(tag.name || `#${tag.id}`),
                    color: tag.color,
                }));
            return { options: [...options, ...foreignSelected], selected: [...selectedFromOptions, ...foreignSelected] };
        }

        const selected = options.filter(row => {
            const id = value?.id ?? value ?? null;
            return id === row.value;
        });

        // Overdue is set automatically based on due_date — never a manual
        // choice. Still shown as the selected value if the ticket already
        // is Overdue, just not offered as something to switch to.
        if (item === 'status') {
            const selectableOptions = options.filter(row => row.label?.toLowerCase() !== 'overdue');
            return { options: selectableOptions, selected };
        }

        return { options, selected };
    };

    const ItemView = ({ item, value, className }) => {

        let options = [];
        let selected = [];
        let intermediateValue = value;

        switch (item) {
            case 'status':
            case 'department':
            case 'type':
            case 'priority':
            case 'tag':
                ({ options, selected } = getLabeledOptions(item, intermediateValue));
                const isMulti = item === 'tag';
                const isClearable = ['type', 'priority', 'tag'].includes(item);
                const selectComponents = isMulti
                    ? { Option: colorOption, MultiValueLabel: colorValue }
                    : { Option: colorOption, SingleValue: colorValueSingle };
                return (
                    <Form.Group className={className}>
                        <Form.Label className="mb-1">{ucFirst(item)}:</Form.Label>
                        <Select
                            isMulti={isMulti}
                            isClearable={isClearable}
                            options={options}
                            value={isMulti ? selected : (selected[0] ?? null)}
                            components={selectComponents}
                            placeholder={`Select ${ucFirst(item)}`}
                            onChange={(value) => handleChange(item, value ?? [])}
                            classNamePrefix="select-component"
                            className="flex-grow-1"
                            isDisabled={ticketIsLoading || isLoading[item] || isClosed}
                        />
                    </Form.Group>
                );
            case 'assignee':
                ({ options, selected } = getLabeledOptions(item, intermediateValue));
                return (
                    <Form.Group className={className}>
                        <Form.Label className="mb-1">Assignee:</Form.Label>
                        <Select
                            isClearable={true}
                            options={options}
                            value={selected[0] ?? null}
                            components={{ Option: imageOption }}
                            placeholder={`Select ${ucFirst(item)}`}
                            onChange={(value) => handleChange(item, value)}
                            classNamePrefix="select-component"
                            className="flex-grow-1"
                            isDisabled={ticketIsLoading || isLoading[item] || isClosed}
                            filterOption={(option, input) => {
                                return option.label?.toLowerCase().includes(input.toLowerCase()) ||
                                    option.data?.description?.toLowerCase().includes(input.toLowerCase());
                            }}
                        />
                    </Form.Group>
                );

            case 'assignees':
                ({ options, selected } = getLabeledOptions(item, intermediateValue));
                return (
                    <Form.Group className={className}>
                        <Form.Label className="mb-1">Assignees:</Form.Label>
                        <Select
                            isMulti={true}
                            isClearable={false}
                            options={options}
                            value={selected}
                            components={{ Option: imageOption }}
                            placeholder={`Select ${ucFirst(item)}`}
                            onChange={(value) => handleChange(item, value)}
                            classNamePrefix="select-component"
                            className="flex-grow-1"
                            isDisabled={ticketIsLoading || isLoading[item] || isClosed || isCustomer}
                        />
                    </Form.Group>
                );

            case 'due_date': {
                selected = intermediateValue ? new Date(intermediateValue) : null;
                const minDueDate = ticket?.created_at ? earliestDueDate(ticket.created_at) : undefined;
                return (
                    <Form.Group className={className}>
                        <Form.Label className="mb-1">Due Date:</Form.Label>
                        <DatePicker
                            handleDateChange={(value) => handleChange(item, parseDate(value))}
                            selected={intermediateValue ? new Date(intermediateValue) : null}
                            minDate={minDueDate}
                            invalid={!!dueDateError}
                            disabled={
                                ticketIsLoading || isLoading[item] || isClosed || dueDateLocked
                            }
                        />
                        {dueDateLocked && !isClosed && (
                            <Form.Text className="text-muted">
                                Only the ticket creator or an admin can change the due date once it&apos;s set.
                            </Form.Text>
                        )}
                        {dueDateError && (
                            <Form.Text className="text-danger">{dueDateError}</Form.Text>
                        )}
                        {!dueDateError && !dueDateLocked && !isClosed && minDueDate && (
                            <Form.Text className="text-muted">
                                Due date must be at least {MIN_DUE_DATE_DAYS_AHEAD} working days after the ticket was created.
                            </Form.Text>
                        )}
                    </Form.Group>
                )
            }
            default:
                return null;
        }
    }

    return (
        <>
            {isTransferredOut ? (
                <p className="text-muted small mb-3">
                    This ticket was transferred. Properties are read-only.
                </p>
            ) : isRejected ? (
                <p className="text-muted small mb-3">
                    This ticket is rejected. You can add replies and notes, but
                    cannot change ticket properties.
                </p>
            ) : isClosedTicket ? (
                <p className="text-muted small mb-3">
                    This ticket is closed. You can add replies and notes, but
                    cannot change ticket properties.
                </p>
            ) : null}
            <ItemView item="due_date" value={ticket.due_date} className="mb-3" />
            <ItemView item="assignees" value={ticket.assignees ?? []} className="mb-3" />
            <ItemView item="status" value={ticket.status} className="mb-3" />
            <ItemView item="department" value={ticket.department} className="mb-3" />
            <ItemView item="priority" value={ticket.priority} className="mb-3" />
            <ItemView item="type" value={ticket.type} className="mb-3" />
            <ItemView item="tag" value={ticket.tags} />
        </>
    );
};

// const MemorizedTicketProperties = useMemo(TicketProperties);

export default TicketProperties;
